import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { ApiQuest } from "griptonite-api-client/types";

import { useDeleteQuestMutation, useQuestEntriesQuery } from "@/hooks/quests";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { useModal } from "./useModal";

export default function ConfirmDeleteQuest({ quest }: { quest: ApiQuest }) {
  const { close } = useModal();
  const venueUuid = useCurrentVenueUuid();
  const mutation = useDeleteQuestMutation(venueUuid);
  const { data: entries } = useQuestEntriesQuery(quest.uuid);

  const onConfirm = () =>
    mutation.mutate(quest.uuid, {
      onSuccess: close,
      onError: () => {
        alert("Failed to delete quest. Please try again or contact support");
      },
    });

  const hasEntries = !!entries?.length;
  // Until the entries have loaded we can't tell, so don't offer the delete.
  const canDelete = !!entries && !hasEntries;

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Are you sure?</DialogTitle>
      <DialogContent>
        <Typography sx={{ maxWidth: 350 }}>
          Deleting this quest will remove it and its rewards permanently. <br />
          {hasEntries ? (
            <Alert color="danger" sx={{ marginTop: 2 }}>
              This quest has entries and cannot be deleted using this tool. Please contact support
              if you need to remove it.
            </Alert>
          ) : (
            <strong>(Once a quest has entries, it cannot be deleted using this tool)</strong>
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          color="danger"
          onClick={onConfirm}
          sx={{ flex: 1 }}
          loading={mutation.isPending}
          disabled={!canDelete}
        >
          Continue
        </Button>
        <Button color="neutral" variant="soft" onClick={close} sx={{ flex: 1 }}>
          Cancel
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}
